import { GS1_AI } from '@myplayer/gs1';
import { type IdentifierRecord } from '@myplayer/schema';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { type AdminUserSession } from './auth-context.js';
import { getFirebaseClients } from './firebase.js';
import {
  isQualifierOverrideActive,
  removeStagedQualifierInFirestore,
  resolveQualifierDisplayTitle,
  resolveQualifierDisplayUrl,
  resolveQualifierOption,
  resolveQualifierPrimaryPair,
  upsertStagedQualifierInFirestore,
  validateQualifierOverrideInput,
  type QualifierOverrideRecord,
  type SupportedQualifierAi,
} from './product-qualifiers-helpers.js';
import { formatPlainAdminError } from './strings.js';

export interface UseProductQualifiersParams {
  readonly selectedRecord: IdentifierRecord;
  readonly session: AdminUserSession | null;
  readonly isControlled?: boolean | undefined;
  readonly initialQualifiers?: readonly QualifierOverrideRecord[] | undefined;
  readonly onSaved?: ((message: string) => void) | undefined;
  readonly onModalOpenChange?: ((open: boolean) => void) | undefined;
}

export interface UseProductQualifiersResult {
  readonly activeRecords: readonly QualifierOverrideRecord[];
  readonly isModalOpen: boolean;
  readonly editingQualifierKey: string | null;
  readonly qualifierCode: SupportedQualifierAi;
  readonly setQualifierCode: (code: SupportedQualifierAi) => void;
  readonly qualifierValue: string;
  readonly setQualifierValue: (val: string) => void;
  readonly qualifierUrl: string;
  readonly setQualifierUrl: (val: string) => void;
  readonly qualifierLabel: string;
  readonly setQualifierLabel: (val: string) => void;
  readonly formError: string | null;
  readonly saving: boolean;
  readonly handleOpenAddModal: () => void;
  readonly handleOpenEditModal: (rec: QualifierOverrideRecord) => void;
  readonly handleCloseModal: () => void;
  readonly handleSaveQualifier: (e: FormEvent) => Promise<void>;
  readonly handleRemoveQualifier: (rec: QualifierOverrideRecord) => Promise<void>;
}

export function useProductQualifiers({
  selectedRecord,
  session,
  isControlled = false,
  initialQualifiers,
  onSaved,
  onModalOpenChange,
}: UseProductQualifiersParams): UseProductQualifiersResult {
  const [recordsByProduct, setRecordsByProduct] = useState<
    Readonly<Record<string, readonly QualifierOverrideRecord[]>>
  >(() =>
    initialQualifiers !== undefined ? { [selectedRecord.identifierKey]: initialQualifiers } : {},
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQualifierKey, setEditingQualifierKey] = useState<string | null>(null);
  const [qualifierCode, setQualifierCode] = useState<SupportedQualifierAi>(GS1_AI.BATCH_LOT);
  const [qualifierValue, setQualifierValue] = useState('');
  const [qualifierUrl, setQualifierUrl] = useState('');
  const [qualifierLabel, setQualifierLabel] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setModalOpen = (next: boolean): void => {
    setIsModalOpen(next);
    onModalOpenChange?.(next);
  };

  useEffect(() => {
    if (initialQualifiers !== undefined) {
      setRecordsByProduct((prev) => ({
        ...prev,
        [selectedRecord.identifierKey]:
          prev[selectedRecord.identifierKey] ??
          initialQualifiers.filter((q) => q.identifierKey === selectedRecord.identifierKey),
      }));
      return;
    }
    if (isControlled || !session) {
      return;
    }
    const { db } = getFirebaseClients();
    const targetWorkspaceId = selectedRecord.workspaceId || session.activeWorkspaceId;
    const colRef = collection(
      db,
      'organisations',
      session.orgId,
      'workspaces',
      targetWorkspaceId,
      'identifiers',
      selectedRecord.identifierKey,
      'qualifiers',
    );
    return onSnapshot(
      colRef,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (docSnap) =>
            ({
              qualifierKey: docSnap.id,
              ...docSnap.data(),
            }) as QualifierOverrideRecord,
        );
        docs.sort((a, b) => a.qualifierKey.localeCompare(b.qualifierKey));
        setRecordsByProduct((prev) => ({
          ...prev,
          [selectedRecord.identifierKey]: docs,
        }));
      },
      (err) => {
        setFormError(formatPlainAdminError(err.message, 'Unable to load qualifier overrides.'));
      },
    );
  }, [
    isControlled,
    initialQualifiers,
    session?.orgId,
    session?.activeWorkspaceId,
    selectedRecord.workspaceId,
    selectedRecord.identifierKey,
  ]);

  const allRecords = useMemo(
    () => recordsByProduct[selectedRecord.identifierKey] ?? [],
    [recordsByProduct, selectedRecord.identifierKey],
  );
  const activeRecords = useMemo(
    () => allRecords.filter((rec) => isQualifierOverrideActive(rec)),
    [allRecords],
  );
  const existingQualifierKeys = useMemo(
    () => new Set(allRecords.map((r) => r.qualifierKey)),
    [allRecords],
  );

  const handleOpenAddModal = (): void => {
    setEditingQualifierKey(null);
    setQualifierCode(GS1_AI.BATCH_LOT);
    setQualifierValue('');
    setQualifierUrl('');
    setQualifierLabel('');
    setFormError(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (rec: QualifierOverrideRecord): void => {
    const pair = resolveQualifierPrimaryPair(rec);
    const url = resolveQualifierDisplayUrl(rec);
    const defaultLabel = `${resolveQualifierOption(pair.code).shortLabel} ${pair.value}`.trim();
    const title = resolveQualifierDisplayTitle(rec);
    setEditingQualifierKey(rec.qualifierKey);
    setQualifierCode(pair.code);
    setQualifierValue(pair.value);
    setQualifierUrl(url);
    setQualifierLabel(title === defaultLabel ? '' : title);
    setFormError(null);
    setModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
    setEditingQualifierKey(null);
    setFormError(null);
  };

  const handleSaveQualifier = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    const validation = validateQualifierOverrideInput({
      parentAi: selectedRecord.ai,
      parentPrimaryKey: selectedRecord.primaryKey,
      qualifierCode,
      qualifierValue,
      targetUrl: qualifierUrl,
      ...(qualifierLabel.trim().length > 0 ? { label: qualifierLabel.trim() } : {}),
    });
    if (!validation.ok) {
      setFormError(validation.message);
      return;
    }

    setSaving(true);
    try {
      const uid = session?.uid ?? 'local_admin';
      const nowIso = new Date().toISOString();
      const nextDraft = {
        title: validation.title,
        status: 'ACTIVE' as const,
        defaultUrl: validation.defaultUrl,
        links: validation.links,
        rules: [],
        submittedBy: uid,
        submittedAt: nowIso,
      };

      if (!isControlled && session) {
        const { db } = getFirebaseClients();
        const targetWorkspaceId = selectedRecord.workspaceId || session.activeWorkspaceId;
        await upsertStagedQualifierInFirestore({
          db,
          orgId: session.orgId,
          workspaceId: targetWorkspaceId,
          identifierKey: selectedRecord.identifierKey,
          uid: session.uid,
          parentAi: selectedRecord.ai,
          parentPrimaryKey: selectedRecord.primaryKey,
          qualifierCode,
          qualifierValue,
          targetUrl: qualifierUrl,
          ...(qualifierLabel.trim().length > 0 ? { label: qualifierLabel.trim() } : {}),
          existingQualifierKeys,
        });
        if (editingQualifierKey !== null && editingQualifierKey !== validation.qualifierKey) {
          const previousRecord = allRecords.find((r) => r.qualifierKey === editingQualifierKey);
          if (previousRecord) {
            await removeStagedQualifierInFirestore({
              db,
              orgId: session.orgId,
              workspaceId: targetWorkspaceId,
              identifierKey: selectedRecord.identifierKey,
              uid: session.uid,
              parentAi: selectedRecord.ai,
              parentPrimaryKey: selectedRecord.primaryKey,
              record: previousRecord,
            });
          }
        }
      } else {
        const nextRecord: QualifierOverrideRecord = {
          qualifierKey: validation.qualifierKey,
          identifierKey: selectedRecord.identifierKey,
          orgId: selectedRecord.orgId,
          workspaceId: selectedRecord.workspaceId,
          combinationCode: validation.combinationCode,
          qualifiers: validation.qualifiers,
          label: validation.title,
          status: 'ACTIVE',
          defaultUrl: validation.defaultUrl,
          links: validation.links,
          updatedBy: uid,
          draft: nextDraft,
        };
        setRecordsByProduct((prev) => {
          const current = prev[selectedRecord.identifierKey] ?? [];
          const filtered = current.filter(
            (r) =>
              r.qualifierKey !== validation.qualifierKey &&
              (editingQualifierKey === null || r.qualifierKey !== editingQualifierKey),
          );
          const updatedList = [...filtered, nextRecord].sort((a, b) =>
            a.qualifierKey.localeCompare(b.qualifierKey),
          );
          return { ...prev, [selectedRecord.identifierKey]: updatedList };
        });
      }

      handleCloseModal();
      onSaved?.('Saved — Live now');
    } catch (err) {
      setFormError(
        formatPlainAdminError(
          err instanceof Error ? err.message : String(err),
          'Could not save qualifier override.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveQualifier = async (rec: QualifierOverrideRecord): Promise<void> => {
    setFormError(null);
    setSaving(true);
    try {
      const uid = session?.uid ?? 'local_admin';
      if (!isControlled && session) {
        const { db } = getFirebaseClients();
        const targetWorkspaceId = selectedRecord.workspaceId || session.activeWorkspaceId;
        await removeStagedQualifierInFirestore({
          db,
          orgId: session.orgId,
          workspaceId: targetWorkspaceId,
          identifierKey: selectedRecord.identifierKey,
          uid: session.uid,
          parentAi: selectedRecord.ai,
          parentPrimaryKey: selectedRecord.primaryKey,
          record: rec,
        });
      } else {
        const nowIso = new Date().toISOString();
        const existingUrl = resolveQualifierDisplayUrl(rec) || 'https://brand.example.com/archived';
        const existingTitle = resolveQualifierDisplayTitle(rec);
        setRecordsByProduct((prev) => {
          const current = prev[selectedRecord.identifierKey] ?? [];
          const updatedList = current.map((item) =>
            item.qualifierKey === rec.qualifierKey
              ? {
                  ...item,
                  status: 'ARCHIVED' as const,
                  updatedBy: uid,
                  draft: {
                    title: existingTitle,
                    status: 'ARCHIVED' as const,
                    defaultUrl: existingUrl,
                    links: item.links ?? [],
                    rules: [],
                    submittedBy: uid,
                    submittedAt: nowIso,
                  },
                }
              : item,
          );
          return { ...prev, [selectedRecord.identifierKey]: updatedList };
        });
      }
      onSaved?.('Saved — Live now');
    } catch (err) {
      setFormError(
        formatPlainAdminError(
          err instanceof Error ? err.message : String(err),
          'Could not remove qualifier override.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    activeRecords,
    isModalOpen,
    editingQualifierKey,
    qualifierCode,
    setQualifierCode,
    qualifierValue,
    setQualifierValue,
    qualifierUrl,
    setQualifierUrl,
    qualifierLabel,
    setQualifierLabel,
    formError,
    saving,
    handleOpenAddModal,
    handleOpenEditModal,
    handleCloseModal,
    handleSaveQualifier,
    handleRemoveQualifier,
  };
}
